/**
 * pinnedStreamStore — the homepage's pinned section, conceptually a
 * fixed-cap stream that sits above the chronological main feed.
 *
 * Thin wrapper around the unified `pin-store`, scoped to
 * `({type: "account"}, "feed")` — the homepage feed surface. Other
 * surfaces (account/tab, collection/default) consume `pin-store`
 * directly.
 *
 * Reads:
 *   pinnedStreamStore.useOrder()           — reactive ordered pin list (feed surface)
 *   pinnedStreamStore.useList()            — split into block/collection ids for render
 *   pinnedStreamStore.isPinned(target)
 *   pinnedStreamStore.useSyncToMainStream() — keep mainStreamStore.blockIds aligned
 *
 * Writes:
 *   pinnedStreamStore.toggle(target)          — pin/unpin + API sync
 *   pinnedStreamStore.unpinIfPresent(blockId) — called on block delete
 *   pinnedStreamStore.warm()                  — prefetch envelopes on app boot
 */
import { useEffect, useMemo } from "react";
import { fetchBlockEnvelope } from "@/api/query-api";
import { getBootstrapPromise } from "@/api/bootstrap-api";
import { blockStore } from "@/data/block-store";
import { mainStreamStore } from "@/data/main-stream-store";
import { bootstrapPinsFromApi, getPinSnapshot, isPinTargetPinned, removePinIfPresent, reorderPinsWithSync, togglePinWithSync, usePinList, replacePins, } from "@/state/pin-store";
const FEED_SCOPE = { type: "account" };
const FEED_SURFACE = "feed";
/**
 * How many pinned block envelopes to prefetch into the cache on app
 * boot. Matches the previous `block-collection.ts` cap so the warm
 * path stays bounded.
 */
const MAX_PIN_PREFETCH = 10;
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
function useOrder() {
    return usePinList(FEED_SCOPE, FEED_SURFACE);
}
/**
 * Split the pin order into id buckets so consumers can render pinned
 * blocks and pinned collections/stacks independently.
 */
function useList() {
    const order = usePinList(FEED_SCOPE, FEED_SURFACE);
    return useMemo(() => {
        const blockIds = [];
        const collectionIds = [];
        for (const p of order) {
            if (p.type === "block")
                blockIds.push(p.id);
            else
                collectionIds.push(p.id);
        }
        return { blockIds, collectionIds, order };
    }, [order]);
}
function isPinned(target) {
    return isPinTargetPinned(FEED_SCOPE, FEED_SURFACE, target);
}
/**
 * Mount-once hook that keeps `mainStreamStore.blockIds` aligned with
 * the pinned-block ids in the current pin order. Backstop for cross-tab
 * `storage` event sync; idempotent.
 */
function useSyncToMainStream() {
    const order = usePinList(FEED_SCOPE, FEED_SURFACE);
    useEffect(() => {
        const blockIds = order.filter((p) => p.type === "block").map((p) => p.id);
        if (blockIds.length === 0)
            return;
        mainStreamStore.includeBlockIds(blockIds);
        void blockStore.ensureLoaded(blockIds);
    }, [order]);
}
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
async function toggle(target) {
    const result = await togglePinWithSync(FEED_SCOPE, FEED_SURFACE, target);
    if (result.ok && result.pinned && target.type === "block") {
        void warmBlockId(target.id);
        mainStreamStore.includeBlockIds([target.id]);
    }
    return result;
}
async function unpinIfPresent(blockId) {
    await removePinIfPresent(FEED_SCOPE, FEED_SURFACE, { type: "block", id: blockId });
}
/**
 * Reorder the pinned list. Indices reference the current
 * `useOrder()` snapshot. Used by the Pinned section's dnd-kit
 * `SortableContext` so pin reorder writes through the rollback-on-
 * API-failure flow in `pin-store.ts` instead of routing through the
 * separate `manualOrderStore` (which is reserved for non-pinned
 * scopes).
 */
async function reorder(fromIndex, toIndex) {
    await reorderPinsWithSync(FEED_SCOPE, FEED_SURFACE, fromIndex, toIndex);
}
// ---------------------------------------------------------------------------
// Warm — prefetch pinned envelopes
//
// Note: an earlier shape of this file installed a `blockCollection.subscribeChanges`
// subscriber that re-upserted any pinned envelope the diff-sync deleted. That
// was a workaround for the `queryCollectionOptions` + `queryFn` configuration
// on `blockCollection` — every queryFn settle (cold seed, focus refetch, any
// invalidation) evicted every envelope NOT in the homepage's top-30. That
// configuration is gone; `blockCollection` is now a pure key-value cache (see
// the file header in `block-collection.ts`) and nothing evicts pinned (or any
// other side-loaded) envelopes anymore. The protection subscriber has been
// removed.
// ---------------------------------------------------------------------------
async function warmBlockId(blockId) {
    if (blockStore.get(blockId))
        return;
    try {
        const env = await fetchBlockEnvelope(blockId);
        if (env)
            blockStore.upsert(env);
    }
    catch {
        // Pin will fall back to `useById`'s network query if the prefetch fails.
    }
}
/**
 * Warm the pinned section from bootstrap.
 *
 * The pin order rides along on the bootstrap payload, so we don't
 * need a separate `GET /v1/pin?scopeType=account&surface=feed`
 * round-trip. Pinned-block envelopes are usually inside the
 * homepage's top-30 surface set returned by
 * `getInitialBlocksPromise()` (the parallel cold-load block fetch),
 * which lands in `blockCollection` — so for most users the prefetch
 * below is a cache hit and fires zero requests. The few cases where
 * a pin points at an older block off the top of the feed still go
 * through `blockStore.ensureLoaded`, which dedupes per id.
 *
 * Falls back to the granular `/v1/pin` endpoint if bootstrap fails —
 * we still want pins to render on a transient network blip.
 */
async function warm() {
    try {
        const payload = await getBootstrapPromise();
        const items = payload.pins.map((p) => ({
            type: p.itemType,
            id: p.itemId,
        }));
        // Guard against an empty server response clobbering a populated
        // local cache. A 200 with `pins: []` shouldn't be possible under
        // normal operation, but transient ACL edge cases / partial
        // bootstrap responses have caused the pinned section to "blink
        // off" on reload in the past. Treat empty-from-server as "trust
        // local until proven otherwise" — same rule
        // `bootstrapPinsFromApi` already follows on the fallback path.
        // The genuine first-ever-login case (no local, no server) still
        // lands here with `local.length === 0` and writes an empty array.
        const local = getPinSnapshot(FEED_SCOPE, FEED_SURFACE);
        if (items.length > 0 || local.length === 0) {
            replacePins(FEED_SCOPE, FEED_SURFACE, items);
        }
        else {
            console.warn("[pinnedStreamStore.warm] bootstrap returned empty pins but local cache has", local.length, "— preserving local.");
        }
    }
    catch (e) {
        console.warn("[pinnedStreamStore.warm] bootstrap failed, falling back:", e);
        await bootstrapPinsFromApi(FEED_SCOPE, FEED_SURFACE);
    }
    const pinned = getPinSnapshot(FEED_SCOPE, FEED_SURFACE);
    const blockIds = pinned
        .filter((p) => p.type === "block")
        .map((p) => p.id)
        .slice(0, MAX_PIN_PREFETCH);
    if (blockIds.length === 0)
        return;
    // `ensureLoaded` only fetches envelopes the cache doesn't already
    // have — pinned blocks that rode along on the initial-blocks fetch
    // are no-ops here.
    await blockStore.ensureLoaded(blockIds);
    mainStreamStore.includeBlockIds(blockIds);
}
// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const pinnedStreamStore = {
    useOrder,
    useList,
    isPinned,
    useSyncToMainStream,
    toggle,
    unpinIfPresent,
    reorder,
    warm,
};
